import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, Typography, Button, Paper, TextField, Snackbar, 
  CircularProgress, IconButton, Badge, Drawer, Divider,
  CardMedia, CardContent, Grid, Chip, CssBaseline
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import MapIcon from '@mui/icons-material/Map';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { InputAdornment } from '@mui/material';
import axios from 'axios';
import PublicMapComponent from '../components/PublicMapComponent';
import FilterPanel from '../components/FilterPanel';
import brandTheme from '../theme';
import Logo from '../assets/logo_bms.png';

const API_ROOT = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ? 'http://localhost:8000'
  : (process.env.REACT_APP_API_BASE_URL ? process.env.REACT_APP_API_BASE_URL.replace(/\/api\/espacios.*$/, '') : 'http://localhost:8000');

const PUBLIC_API = `${API_ROOT}/api/public/espacios`;
const LEADS_API = `${API_ROOT}/api/leads`;

const PublicPortal = () => {
  const [espacios, setEspacios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [filters, setFilters] = useState({ tipo_espacio: [], tamano: [], zona: [], provincia: [] });
  const [filterOptions, setFilterOptions] = useState({ tipo_espacio: [], tamano: [], zona: [], provincia: [] });
  const [mapsApiKey, setMapsApiKey] = useState(null);
  
  // Lead Form State
  const [leadForm, setLeadForm] = useState({ client_name: '', email: '', phone: '', company: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(PUBLIC_API);
      setEspacios(response.data);
      
      const options = {
        tipo_espacio: [...new Set(response.data.map(i => i.Tipo_Espacio))].filter(Boolean).sort(),
        tamano: [...new Set(response.data.map(i => i.Tamano))].filter(Boolean).sort(),
        zona: [...new Set(response.data.map(i => i.Zona))].filter(Boolean).sort(),
        provincia: [...new Set(response.data.map(i => i.Provincia))].filter(Boolean).sort(),
      };
      setFilterOptions(options);

      // Fetch Maps API Key
      try {
        const keyResponse = await axios.get(`${API_ROOT}/api/config/maps-key`);
        setMapsApiKey(keyResponse.data.apiKey);
      } catch (keyErr) {
        console.error("Error fetching Maps API Key:", keyErr);
      }
    } catch (error) {
      console.error("Error fetching public data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleCartItem = (space) => {
    setCart(prev => {
      const exists = prev.find(item => item.Codigo === space.Codigo);
      if (exists) return prev.filter(item => item.Codigo !== space.Codigo);
      return [...prev, space];
    });
    setSnackbar({ open: true, message: `Valla ${space.Codigo} añadida`, severity: 'success' });
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (cart.length === 0) {
      setSnackbar({ open: true, message: 'Selecciona al menos una valla', severity: 'warning' });
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...leadForm, selected_billboards: cart.map(item => item.Codigo) };
      await axios.post(LEADS_API, payload);
      setSnackbar({ open: true, message: '¡Solicitud enviada con éxito!', severity: 'success' });
      setCart([]);
      setLeadForm({ client_name: '', email: '', phone: '', company: '', message: '' });
      setIsCartOpen(false);
    } catch (error) {
      setSnackbar({ open: true, message: 'Error al enviar solicitud', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const filteredEspacios = espacios.filter(s => {
    if (filters.tipo_espacio.length > 0 && !filters.tipo_espacio.includes(s.Tipo_Espacio)) return false;
    if (filters.tamano.length > 0 && !filters.tamano.includes(s.Tamano)) return false;
    if (filters.zona.length > 0 && !filters.zona.includes(s.Zona)) return false;
    if (filters.provincia.length > 0 && !filters.provincia.includes(s.Provincia)) return false;
    return true;
  });

  return (
    <ThemeProvider theme={brandTheme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', width: '100vw', overflow: 'hidden' }}>
        
        {/* AppBar similar to app */}
        <Paper elevation={0} sx={{ bgcolor: 'primary.main', borderRadius: 0, p: 1.5, display: 'flex', alignItems: 'center' }}>
          <img src={Logo} alt="Logo" style={{ height: '40px', marginRight: '15px' }} />
          <Typography variant="h6" sx={{ flexGrow: 1, color: 'white', fontWeight: 800 }}>CATÁLOGO DE VALLAS</Typography>
          <IconButton color="inherit" onClick={() => setIsCartOpen(true)} sx={{ color: 'white' }}>
            <Badge badgeContent={cart.length} color="secondary">
              <ShoppingCartIcon />
            </Badge>
          </IconButton>
        </Paper>

        <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
          
          {/* Sidebar like platform */}
          <Paper elevation={6} sx={{
            width: { xs: '100%', md: '320px' }, p: 2, overflowY: 'auto',
            flexShrink: 0, display: 'flex', flexDirection: 'column',
            bgcolor: 'background.paper', borderRight: '1px solid', borderColor: 'divider'
          }}>
            <FilterPanel 
              filters={filters} 
              onFilterChange={(name, val) => setFilters(p => ({ ...p, [name]: val }))} 
              filterOptions={filterOptions}
              showOnly={['tipo_espacio', 'tamano', 'zona', 'provincia']}
            />
            
            <Divider sx={{ my: 2 }} />
            
            {selectedSpace && (
              <Box>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 700 }}>VALLA SELECCIONADA</Typography>
                <CardMedia component="img" height="140" image={selectedSpace.url_foto} sx={{ borderRadius: 2, mb: 1 }} />
                <Typography variant="h6" fontWeight={700}>Detalles de Ubicación</Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>{selectedSpace.Direccion}</Typography>
                <Button 
                  fullWidth variant="contained" sx={{ mt: 1 }}
                  onClick={() => toggleCartItem(selectedSpace)}
                >
                  {cart.find(i => i.Codigo === selectedSpace.Codigo) ? "Quitar selección" : "Me interesa"}
                </Button>
              </Box>
            )}

            {!selectedSpace && cart.length > 0 && (
              <Box sx={{ mt: 'auto' }}>
                <Button 
                  fullWidth variant="contained" color="secondary" 
                  startIcon={<SendIcon />} onClick={() => setIsCartOpen(true)}
                  sx={{ py: 1.5, fontWeight: 700 }}
                >
                  Solicitar Info ({cart.length})
                </Button>
              </Box>
            )}
          </Paper>

          {/* Map Area */}
          <Box sx={{ flexGrow: 1, position: 'relative' }}>
            {loading || !mapsApiKey ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', gap: 2 }}>
                <CircularProgress />
                {!mapsApiKey && !loading && <Typography variant="caption">Cargando mapa...</Typography>}
              </Box>
            ) : (
              <PublicMapComponent 
                espacios={filteredEspacios} 
                onSpaceSelect={setSelectedSpace} 
                selectedSpaceFromApp={selectedSpace} 
                onAddToCart={toggleCartItem}
                cart={cart}
                mapsApiKey={mapsApiKey}
              />
            )}
          </Box>
        </Box>

        {/* Lead/Cart Drawer */}
        <Drawer anchor="right" open={isCartOpen} onClose={() => setIsCartOpen(false)}>
          <Box sx={{ width: { xs: '100vw', sm: 350 }, p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" fontWeight={700}>Tu Interés ({cart.length})</Typography>
              <IconButton onClick={() => setIsCartOpen(false)}><CloseIcon /></IconButton>
            </Box>
            <Divider sx={{ mb: 2 }} />
            
            {cart.map((item, idx) => (
              <Box key={item.Codigo} sx={{ display: 'flex', gap: 2, mb: 1.5, alignItems: 'center' }}>
                <Box component="img" src={item.url_foto} sx={{ width: 50, height: 35, borderRadius: 1, objectFit: 'cover' }} />
                <Typography variant="body2" sx={{ flexGrow: 1 }}>Opción {idx + 1}</Typography>
                <IconButton size="small" onClick={() => toggleCartItem(item)}><CloseIcon fontSize="inherit" /></IconButton>
              </Box>
            ))}

            {cart.length > 0 && (
              <Box component="form" onSubmit={handleLeadSubmit} sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                  <EditNoteIcon color="primary" />
                  <Typography variant="subtitle2" fontWeight={800} color="primary.main">
                    Déjanos tus datos sin compromiso
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2.5 }}>
                  Un asesor se pondrá en contacto contigo pronto para brindarte más detalles.
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField 
                      size="small" required fullWidth label="Nombre Completo" variant="outlined"
                      value={leadForm.client_name} onChange={e => setLeadForm(p => ({ ...p, client_name: e.target.value }))} 
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><PersonOutlineIcon fontSize="small" /></InputAdornment>,
                        sx: { borderRadius: 2, bgcolor: '#f8f9fa' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField 
                      size="small" required fullWidth label="Correo Electrónico" type="email" variant="outlined"
                      value={leadForm.email} onChange={e => setLeadForm(p => ({ ...p, email: e.target.value }))} 
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><MailOutlineIcon fontSize="small" /></InputAdornment>,
                        sx: { borderRadius: 2, bgcolor: '#f8f9fa' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField 
                      size="small" fullWidth label="Empresa (Opcional)" variant="outlined"
                      value={leadForm.company} onChange={e => setLeadForm(p => ({ ...p, company: e.target.value }))} 
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><BusinessIcon fontSize="small" /></InputAdornment>,
                        sx: { borderRadius: 2, bgcolor: '#f8f9fa' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField 
                      size="small" fullWidth label="WhatsApp o Teléfono (Opcional)" variant="outlined"
                      value={leadForm.phone} onChange={e => setLeadForm(p => ({ ...p, phone: e.target.value }))} 
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><PhoneOutlinedIcon fontSize="small" /></InputAdornment>,
                        sx: { borderRadius: 2, bgcolor: '#f8f9fa' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField 
                      size="small" fullWidth multiline rows={3} label="¿Algún detalle adicional?" variant="outlined"
                      value={leadForm.message} onChange={e => setLeadForm(p => ({ ...p, message: e.target.value }))} 
                      placeholder="Escribe aquí cualquier consulta o la zona de tu interés..."
                      InputProps={{
                        sx: { borderRadius: 2, bgcolor: '#f8f9fa' }
                      }}
                    />
                  </Grid>
                </Grid>
                <Button 
                  fullWidth variant="contained" type="submit" disabled={submitting} 
                  sx={{ 
                    mt: 3, py: 1.5, borderRadius: 2, fontWeight: 800, fontSize: '0.95rem',
                    background: 'linear-gradient(45deg, #0057B7 30%, #00AEEF 90%)',
                    color: 'white',
                    boxShadow: '0 4px 15px rgba(0,174,239,0.3)',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(0,174,239,0.4)',
                    }
                  }}
                  startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon fontSize="small" />}
                >
                  {submitting ? "ENVIANDO..." : "ENVIAR CONSULTA"}
                </Button>
              </Box>
            )}
          </Box>
        </Drawer>

        <Snackbar 
          open={snackbar.open} autoHideDuration={4000} 
          onClose={() => setSnackbar(p => ({ ...p, open: false }))}
          message={snackbar.message}
        />
      </Box>
    </ThemeProvider>
  );
};

export default PublicPortal;
